import type { MealSuggestionRequest, MealSuggestionResponse } from '@recipes/contracts';
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@recipes/ui';
import { useMutation } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormField } from '@/features/shared/form-field';
import { AiConsentNotice } from '@/features/shared/ai-consent-notice';
import { useAiConsent } from '@/features/shared/hooks';
import { mealSlotLabel } from '@/features/shared/labels';
import { ProcessingIndicator } from '@/features/shared/processing-indicator';
import { api } from '@/lib/api';
import { describeError } from '@/lib/errors';
import { STANDARD_SLOTS } from './week-grid';

type Suggestion = MealSuggestionResponse['suggestions'][number];

export function SuggestionsDialog({
  open,
  onOpenChange,
  from,
  to,
  onAccept,
  accepting,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  from: string;
  to: string;
  onAccept: (suggestions: Suggestion[]) => Promise<void>;
  accepting?: boolean;
}) {
  const { t } = useTranslation();
  const consented = useAiConsent();
  const [slots, setSlots] = useState<string[]>(['DINNER']);
  const [usePantry, setUsePantry] = useState(true);
  const [maxMinutes, setMaxMinutes] = useState('');
  const [accepted, setAccepted] = useState<Set<number>>(new Set());

  const mutation = useMutation({
    mutationFn: (body: MealSuggestionRequest) => api.ai.mealSuggestions(body),
    onSuccess: () => setAccepted(new Set()),
  });
  const suggestions = mutation.data?.suggestions ?? [];

  const accept = async (indexes: number[]) => {
    const list = indexes.map((i) => suggestions[i]).filter((s): s is Suggestion => Boolean(s));
    await onAccept(list);
    setAccepted((prev) => new Set([...prev, ...indexes]));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={t('common.close')} fullscreenOnMobile>
        <DialogHeader>
          <DialogTitle>{t('mealPlan.suggestions.title')}</DialogTitle>
          <DialogDescription>{t('mealPlan.suggestions.description')}</DialogDescription>
        </DialogHeader>
        {!consented && <AiConsentNotice variant="inline" />}
        <form
          noValidate
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            mutation.mutate({
              dateFrom: from,
              dateTo: to,
              slots,
              usePantry,
              maxMinutesPerMeal: maxMinutes ? Number(maxMinutes) : undefined,
            });
          }}
        >
          <fieldset>
            <legend className="text-sm font-medium">{t('mealPlan.suggestions.slots')}</legend>
            <div className="mt-1 flex flex-wrap gap-2">
              {STANDARD_SLOTS.map((s) => (
                <label
                  key={s}
                  className="flex min-h-[36px] items-center gap-2 rounded-full border px-3 text-sm"
                >
                  <Checkbox
                    checked={slots.includes(s)}
                    onCheckedChange={(c) =>
                      setSlots((prev) => (c === true ? [...prev, s] : prev.filter((x) => x !== s)))
                    }
                  />
                  {mealSlotLabel(t, s)}
                </label>
              ))}
            </div>
          </fieldset>
          <label className="flex min-h-[40px] items-center gap-3 text-sm">
            <Checkbox checked={usePantry} onCheckedChange={(c) => setUsePantry(c === true)} />
            {t('mealPlan.suggestions.usePantry')}
          </label>
          <FormField
            label={t('mealPlan.suggestions.maxMinutes')}
            optionalText={t('common.optional')}
            className="max-w-[12rem]"
          >
            <Input
              type="number"
              min={5}
              inputMode="numeric"
              value={maxMinutes}
              onChange={(e) => setMaxMinutes(e.target.value)}
            />
          </FormField>
          <Button
            type="submit"
            loading={mutation.isPending}
            disabled={!consented || slots.length === 0}
          >
            <Sparkles aria-hidden /> {t('mealPlan.suggestions.generate')}
          </Button>
        </form>
        {mutation.isPending && <ProcessingIndicator label={t('mealPlan.suggestions.working')} />}
        {mutation.error && <Alert variant="destructive">{describeError(mutation.error)}</Alert>}
        {mutation.data && suggestions.length === 0 && (
          <p className="text-sm text-muted-foreground">{t('common.noResults')}</p>
        )}
        {suggestions.length > 0 && (
          <ul
            className="max-h-72 space-y-2 overflow-y-auto"
            aria-label={t('mealPlan.suggestions.results')}
          >
            {suggestions.map((s, i) => (
              <li key={i} className="rounded-md border p-2 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{s.title}</span>
                  <Badge variant="muted">
                    {s.date} · {mealSlotLabel(t, s.slot)}
                  </Badge>
                  {!s.recipeId && (
                    <Badge variant="warning">{t('mealPlan.suggestions.noRecipe')}</Badge>
                  )}
                  {accepted.has(i) && (
                    <Badge variant="success">{t('mealPlan.suggestions.added')}</Badge>
                  )}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{s.reason}</p>
                {!accepted.has(i) && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="mt-2"
                    loading={accepting}
                    onClick={() => void accept([i])}
                  >
                    {t('mealPlan.suggestions.accept')}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.close')}
          </Button>
          {suggestions.length > 0 && (
            <Button
              type="button"
              loading={accepting}
              disabled={accepted.size === suggestions.length}
              onClick={() =>
                void accept(suggestions.map((_, i) => i).filter((i) => !accepted.has(i)))
              }
            >
              {t('mealPlan.suggestions.acceptAll')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
